/**
 * Every call the access panel makes, in one file, so the set of endpoints it depends on is readable.
 *
 * All writes return `ApiResponse`, and the caller re-reads rather than patching local state — see
 * AccessDashboard. Nothing here swallows an error: the refusals ARE the feature (a 409 names the client
 * that already holds a contested domain), and a helper that reduced them to `false` would delete the
 * only guidance the operator gets.
 */
import { adminFetch, type ApiResponse } from '../adminFetch';
import type {
  AuditResponse,
  CatalogueResponse,
  OrgDetail,
  OrgListResponse,
  ResourceType,
  SegmentsResponse,
} from './types';

const BASE = '/admin/access';

export const fetchOrganizations = (): Promise<ApiResponse<OrgListResponse>> =>
  adminFetch<OrgListResponse>(`${BASE}/organizations`);

export const fetchOrganization = (id: number): Promise<ApiResponse<OrgDetail>> =>
  adminFetch<OrgDetail>(`${BASE}/organizations/${id}`);

export const fetchSegments = (): Promise<ApiResponse<SegmentsResponse>> =>
  adminFetch<SegmentsResponse>(`${BASE}/segments`);

export const fetchCatalogue = (): Promise<ApiResponse<CatalogueResponse>> =>
  adminFetch<CatalogueResponse>(`${BASE}/catalogue`);

export const fetchAudit = (limit = 25): Promise<ApiResponse<AuditResponse>> =>
  adminFetch<AuditResponse>(`${BASE}/audit?limit=${limit}`);

export const attachDomain = (orgId: number, domain: string) =>
  adminFetch<unknown>(`${BASE}/organizations/${orgId}/domains`, 'POST', { domain });

/**
 * The domain goes in the PATH, so it is encoded. A domain is not arbitrary text, but encoding it costs
 * nothing and a path segment assembled by concatenation is how a stray character becomes a 404 that
 * looks like a missing row.
 */
export const detachDomain = (orgId: number, domain: string) =>
  adminFetch<unknown>(
    `${BASE}/organizations/${orgId}/domains/${encodeURIComponent(domain)}`,
    'DELETE',
  );

/**
 * Segments, audiences, surveys and survey groups grant by ID; populations grant by KEY.
 *
 * Split at the call site rather than inside, so a caller cannot pass a population id: there is no such
 * thing, the backend answers 422, and a grant carrying one resolves to nothing.
 */
export const grantByIds = (orgId: number, resourceType: ResourceType, ids: number[]) =>
  adminFetch<unknown>(`${BASE}/organizations/${orgId}/grants`, 'POST', {
    resource_type: resourceType,
    resource_ids: ids,
  });

export const grantByKeys = (orgId: number, resourceType: ResourceType, keys: string[]) =>
  adminFetch<unknown>(`${BASE}/organizations/${orgId}/grants`, 'POST', {
    resource_type: resourceType,
    resource_keys: keys,
  });

export const revokeGrant = (grantId: number) =>
  adminFetch<unknown>(`${BASE}/grants/${grantId}`, 'DELETE');

export const setUseCases = (orgId: number, useCases: string[], effect: 'allow' | 'deny') =>
  adminFetch<unknown>(`${BASE}/organizations/${orgId}/use-cases`, 'POST', {
    use_cases: useCases,
    effect,
  });

/** Clears the verdict entirely — distinct from denying. Without it, a deny outlives a plan upgrade. */
export const clearUseCase = (orgId: number, useCase: string) =>
  adminFetch<unknown>(
    `${BASE}/organizations/${orgId}/use-cases/${encodeURIComponent(useCase)}`,
    'DELETE',
  );

/**
 * Whether new studies in this workspace are shared with colleagues.
 *
 * `PATCH` because it is one field of the organization, alongside name and status, rather than a resource of
 * its own. The endpoint refuses any value other than `private`/`workspace` rather than coercing it, so a
 * typo surfaces here as a 422 instead of silently leaving the workspace private.
 */
export const setSimulationVisibility = (orgId: number, visibility: string) =>
  adminFetch<unknown>(`${BASE}/organizations/${orgId}`, 'PATCH', {
    default_simulation_visibility: visibility,
  });

export const revertAudit = (auditId: number) =>
  adminFetch<unknown>(`${BASE}/audit/${auditId}/revert`, 'POST');

/**
 * The first step of onboarding. Returns the new id so the panel can select it immediately — the next
 * thing the operator needs is that client's domain and grants, not a list to hunt through.
 */
export const createOrganization = (slug: string, name: string) =>
  adminFetch<{ id: number; slug: string; name: string }>(`${BASE}/organizations`, 'POST', {
    slug,
    name,
  });
